<template>
  <div class="app-container safety-monitoring">
    <!-- 一、搜索区域 -->
    <el-card shadow="never" class="search-card">
      <el-form ref="filterForm" :inline="true" size="small" @keyup.enter.native="toFilter">
        <el-form-item label="设备名称">
          <el-input v-model="filter.dev_name" placeholder="请输入设备名称" clearable style="width: 180px"></el-input>
        </el-form-item>
        <el-form-item label="设备类型">
          <el-select v-model="filter.dev_type" placeholder="全部" clearable style="width: 140px">
            <el-option label="--全部--" value=""></el-option>
            <el-option v-for="item in devTypeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="资产码">
          <el-input v-model="filter.asset_code" placeholder="请输入资产码" clearable style="width: 200px"></el-input>
        </el-form-item>
        <el-form-item label="站点名称">
          <el-input v-model="filter.site_name" placeholder="请输入站点名称" clearable style="width: 180px"></el-input>
        </el-form-item>
        <el-form-item label="异常名称">
          <el-input v-model="filter.alarm_name" placeholder="请输入异常名称" clearable style="width: 160px"></el-input>
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="toFilter">{{ $t("publics.querybtn") }}</el-button>
          <el-button icon="el-icon-refresh" @click="onResetFilter">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 二、数据统计（仅按搜索条件统计，不含分页条件） -->
    <el-card shadow="never" class="statistic-card">
      <div slot="header" class="card-header">
        <span class="card-title">数据统计</span>
        <span class="card-sub-title">统计当前搜索条件下的监测次数（与分页无关）</span>
      </div>

      <div class="stat-grid" v-loading="statisticLoading">
        <div class="stat-item stat-green">
          <div class="stat-item-label"><i class="stat-item-dot"></i>监测正常次数</div>
          <div class="stat-item-value">
            {{ formatInteger(statistic.normal_num) }}
            <span class="stat-item-unit">次</span>
          </div>
        </div>

        <div class="stat-item stat-orange">
          <div class="stat-item-label"><i class="stat-item-dot"></i>监测预警次数</div>
          <div class="stat-item-value">
            {{ formatInteger(statistic.warn_num) }}
            <span class="stat-item-unit">次</span>
          </div>
        </div>

        <div class="stat-item stat-red">
          <div class="stat-item-label"><i class="stat-item-dot"></i>监测报警次数</div>
          <div class="stat-item-value">
            {{ formatInteger(statistic.alarm_num) }}
            <span class="stat-item-unit">次</span>
          </div>
        </div>

        <div class="stat-item stat-rate" :class="rateLevelClass">
          <div class="stat-item-label">
            <i class="stat-item-dot"></i>异常率
            <el-tooltip effect="dark" placement="top" :content="rateTip">
              <i class="el-icon-question rate-help"></i>
            </el-tooltip>
          </div>
          <div class="stat-item-value">
            {{ abnormalRate }}
            <span class="stat-item-unit">{{ rateUnit }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 三、异常数据分页列表 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="card-header">
        <span class="card-title">异常数据列表</span>
        <span class="card-sub-title">共 {{ pageParams.total }} 条异常记录</span>
      </div>

      <div class="table-wrap">
        <el-table
          ref="mainTable"
          header-cell-class-name="table-header-cell"
          :data="tableData"
          height="100%"
          border
          stripe
          v-loading="loading"
        >
          <el-table-column label="序号" type="index" width="60" align="center" :index="indexMethod"></el-table-column>
          <el-table-column label="设备名称" prop="dev_name" min-width="170" show-overflow-tooltip></el-table-column>
          <el-table-column label="设备类型" prop="dev_type" width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag size="small" type="info">{{ devTypeName(row.dev_type) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="资产码" prop="asset_code" width="190" align="center" show-overflow-tooltip></el-table-column>
          <el-table-column label="所属站点名称" prop="site_name" min-width="160" show-overflow-tooltip></el-table-column>
          <el-table-column label="异常名称" prop="alarm_name" min-width="150" show-overflow-tooltip></el-table-column>
          <el-table-column label="异常类型" prop="alarm_type" width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag size="small" :type="Number(row.alarm_type) === 2 ? 'danger' : 'warning'">
                {{ alarmTypeName(row.alarm_type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="异常编码" prop="alarm_code" width="160" align="center" show-overflow-tooltip></el-table-column>
          <el-table-column label="异常详情" prop="alarm_detail" min-width="240" show-overflow-tooltip></el-table-column>
          <el-table-column label="异常数" prop="alarm_num" width="80" align="center"></el-table-column>
          <el-table-column label="通道" prop="channel" width="80" align="center">
            <template slot-scope="{ row }">{{ formatChannel(row.channel) }}</template>
          </el-table-column>
          <el-table-column label="详细地址" prop="address" min-width="260" show-overflow-tooltip></el-table-column>
          <el-table-column label="异常时间" prop="alarm_time" width="170" align="center"></el-table-column>
        </el-table>
      </div>

      <div class="my-pagination">
        <el-pagination
          background
          layout="sizes,total,prev,pager,next,jumper"
          :total="pageParams.total"
          :page-size="pageParams.limit"
          :page-sizes="[5, 10, 50, 100, 500, 1000]"
          :current-page="pageParams.page"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import pageMixin from "@/mixins/pageMixin"
import { getSafetyMonitorStatisticApi, getSafetyMonitorAbnormalPageListApi } from "@/api/safety"
import { SAFETY_MONITOR_MOCK, formatAbnormalRate } from "@/utils/safety-monitor"

/* ================= 设备类型 ================= */
const DEV_TYPE_OPTIONS = [
  { label: "台区", value: "power_transformer" },
  { label: "一体柜", value: "power_cabinet" },
  { label: "分支箱", value: "power_branch_box" },
  { label: "充电桩", value: "power_charge_pile" }
]

const DEV_TYPE_NAME_MAP = DEV_TYPE_OPTIONS.reduce((map, item) => {
  map[item.value] = item.label
  return map
}, {})

// 异常类型：1 预警，2 报警
const ALARM_TYPE_NAME_MAP = {
  1: "预警",
  2: "报警"
}

export default {
  name: "SafetyMonitoring",
  mixins: [pageMixin],
  data() {
    return {
      // 接口为示例假路径，先用示例数据渲染；接口就绪后置为 false
      useMock: true,
      loading: false,
      statisticLoading: false,
      tableData: [],
      devTypeOptions: DEV_TYPE_OPTIONS,
      filter: {
        dev_name: "",
        dev_type: "",
        asset_code: "",
        site_name: "",
        alarm_name: ""
      },
      statistic: {
        normal_num: 0,
        warn_num: 0,
        alarm_num: 0
      }
    }
  },
  computed: {
    // 监测总次数 = 正常 + 预警 + 报警
    statTotal() {
      return this.statistic.normal_num + this.statistic.warn_num + this.statistic.alarm_num
    },
    // 异常率 =（预警次数 + 报警次数）/（正常次数 + 预警次数 + 报警次数），按数值大小自动切换 %、‰、‱
    rateInfo() {
      return formatAbnormalRate(this.statistic.warn_num, this.statistic.alarm_num, this.statistic.normal_num)
    },
    abnormalRate() {
      return this.rateInfo.value
    },
    rateUnit() {
      return this.rateInfo.unit
    },
    // 异常率说明（鼠标悬停标题旁的问号展示）
    rateTip() {
      return `异常率 =（监测预警次数 + 监测报警次数）/（监测正常次数 + 监测预警次数 + 监测报警次数），数值较小时自动以千分号（‰）或万分号（‱）展示，当前监测总次数 ${this.formatInteger(this.statTotal)} 次`
    },
    rateLevelClass() {
      const rate = this.rateInfo.percent
      if (rate >= 10) return "rate-high"
      if (rate >= 3) return "rate-warn"
      return "rate-ok"
    }
  },
  created() {
    this.getStatistic()
    this.getData()
  },
  methods: {
    devTypeName(value) {
      return DEV_TYPE_NAME_MAP[value] || value || "--"
    },
    alarmTypeName(value) {
      return ALARM_TYPE_NAME_MAP[value] || "--"
    },
    // 通道：无通道展示「无」，有通道展示数字
    formatChannel(value) {
      if (value === null || value === undefined || value === "") return "无"
      return value
    },
    formatInteger(value) {
      const num = Number(value)
      if (isNaN(num)) return "0"
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },
    indexMethod(index) {
      return (this.pageParams.page - 1) * this.pageParams.limit + index + 1
    },
    // 搜索条件（统计与列表共用，不含分页参数）
    buildSearchParams() {
      const params = {}
      const devName = (this.filter.dev_name || "").trim()
      const assetCode = (this.filter.asset_code || "").trim()
      const siteName = (this.filter.site_name || "").trim()
      const alarmName = (this.filter.alarm_name || "").trim()
      if (devName) params.dev_name = devName
      if (this.filter.dev_type) params.dev_type = this.filter.dev_type
      if (assetCode) params.asset_code = assetCode
      if (siteName) params.site_name = siteName
      if (alarmName) params.alarm_name = alarmName
      return params
    },
    // 覆盖 pageMixin：查询时同时刷新统计与列表
    toFilter() {
      this.pageParams.page = 1
      this.getStatistic()
      this.getData()
    },
    onResetFilter() {
      this.filter = {
        dev_name: "",
        dev_type: "",
        asset_code: "",
        site_name: "",
        alarm_name: ""
      }
      this.toFilter()
    },
    // 示例数据按搜索条件过滤
    filterMockList() {
      const devName = (this.filter.dev_name || "").trim()
      const assetCode = (this.filter.asset_code || "").trim()
      const siteName = (this.filter.site_name || "").trim()
      const alarmName = (this.filter.alarm_name || "").trim()
      const devType = this.filter.dev_type
      return SAFETY_MONITOR_MOCK.list.filter((item) => {
        if (devName && item.dev_name.indexOf(devName) === -1) return false
        if (devType && item.dev_type !== devType) return false
        if (assetCode && item.asset_code.indexOf(assetCode) === -1) return false
        if (siteName && item.site_name.indexOf(siteName) === -1) return false
        if (alarmName && item.alarm_name.indexOf(alarmName) === -1) return false
        return true
      })
    },
    // 数据统计（只依赖搜索条件，不依赖分页）
    getStatistic() {
      if (this.useMock) {
        const matched = this.filterMockList()
        const ratio = SAFETY_MONITOR_MOCK.list.length ? matched.length / SAFETY_MONITOR_MOCK.list.length : 0
        this.statistic = {
          normal_num: Math.round(SAFETY_MONITOR_MOCK.normal_num * ratio),
          warn_num: matched.filter((item) => Number(item.alarm_type) === 1).length,
          alarm_num: matched.filter((item) => Number(item.alarm_type) === 2).length
        }
        return
      }

      this.statisticLoading = true
      getSafetyMonitorStatisticApi(this.buildSearchParams())
        .then((res) => {
          const data = (res && res.data) || res || {}
          this.statistic = {
            normal_num: Number(data.normal_num) || 0,
            warn_num: Number(data.warn_num) || 0,
            alarm_num: Number(data.alarm_num) || 0
          }
        })
        .catch(() => {
          this.statistic = { normal_num: 0, warn_num: 0, alarm_num: 0 }
        })
        .finally(() => {
          this.statisticLoading = false
        })
    },
    // 分页列表
    getData() {
      if (this.useMock) {
        const matched = this.filterMockList()
        const start = (this.pageParams.page - 1) * this.pageParams.limit
        this.tableData = matched.slice(start, start + this.pageParams.limit)
        this.pageParams.total = matched.length
        return
      }

      this.loading = true
      const params = Object.assign(this.buildSearchParams(), {
        page: this.pageParams.page,
        pagesize: this.pageParams.limit
      })
      getSafetyMonitorAbnormalPageListApi(params)
        .then((res) => {
          this.tableData = (res && res.data) || []
          this.pageParams.total = (res && res.total) || 0
        })
        .catch(() => {
          this.tableData = []
          this.pageParams.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.safety-monitoring {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .search-card,
  .statistic-card {
    flex-shrink: 0;
    margin-bottom: 12px;
  }

  .search-card {
    ::v-deep .el-card__body {
      padding: 10px 16px 0;
    }

    ::v-deep .el-form-item {
      margin-right: 12px;
      margin-bottom: 10px;
    }
  }

  .statistic-card {
    ::v-deep .el-card__body {
      padding: 10px 16px;
    }
  }

  .search-card,
  .statistic-card,
  .table-card {
    ::v-deep .el-card__header {
      padding: 8px 16px;
    }
  }

  .table-card {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    ::v-deep .el-card__body {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      padding: 10px 16px 12px;
    }
  }

  /* 列表区域占满剩余高度，纵向 / 横向滚动条由表格内部产生 */
  .table-wrap {
    flex: 1;
    min-height: 0;
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .card-title {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }

    .card-sub-title {
      font-size: 13px;
      color: #909399;
    }
  }

  /* ========== 数据统计 ========== */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;

    .stat-item {
      position: relative;
      padding: 8px 14px;
      min-height: 56px;
      border: 1px solid #ebeef5;
      border-radius: 6px;
      background-color: #fafcff;
      box-sizing: border-box;
      overflow: hidden;
      transition: box-shadow 0.2s;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background-color: var(--stat-color, #409eff);
      }

      &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      }

      .stat-item-label {
        display: flex;
        align-items: center;
        font-size: 13px;
        line-height: 18px;
        color: #606266;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        .stat-item-dot {
          flex-shrink: 0;
          width: 6px;
          height: 6px;
          margin-right: 6px;
          border-radius: 50%;
          background-color: var(--stat-color, #409eff);
        }

        .rate-help {
          margin-left: 4px;
          font-size: 13px;
          color: #909399;
          cursor: help;

          &:hover {
            color: #409eff;
          }
        }
      }

      .stat-item-value {
        margin-top: 2px;
        font-size: 20px;
        font-weight: 700;
        line-height: 1.3;
        color: #303133;
      }

      .stat-item-unit {
        margin-left: 4px;
        font-size: 12px;
        font-weight: 400;
        color: #909399;
      }
    }

    .stat-green {
      --stat-color: #67c23a;
    }

    .stat-orange {
      --stat-color: #e6a23c;
    }

    .stat-red {
      --stat-color: #f56c6c;
    }

    .stat-rate {
      --stat-color: #909399;

      &.rate-ok {
        --stat-color: #67c23a;
      }

      &.rate-warn {
        --stat-color: #e6a23c;
      }

      &.rate-high {
        --stat-color: #f56c6c;
      }

      .stat-item-value {
        color: var(--stat-color);
      }
    }
  }

  .my-pagination {
    flex-shrink: 0;
    margin-top: 10px;
    text-align: right;
  }
}
</style>
